/* eslint-disable hydrogen/prefer-image-component */
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Image, Link, useUrl} from '@shopify/hydrogen';
import classNames from 'classnames';

import {
  IconsOfWhisky,
  theWhiskyListLogoDesktopSVG,
  theWhiskyListLogoMobileSVG,
  theWhiskyListLogoSmallPNG,
} from '../../assets/img';

import {useModalToggle} from '../../hooks';

import CartMenuButton from '../cart/CartMenuButton.client';

import {
  MainNavigationCategoryName,
  MainNavigationCategoryType,
  SiteTopAlertBarType,
} from '../../types';
import HeaderNavigationMenu from './HeaderNavigationMenu.client';
import {useGlobalContainer} from '../../contexts/GlobalContainerProvider.client';
import {useSearchUI} from '../../contexts/SearchUIProvider.client';
import {useAuthService} from '../../contexts/AuthServiceProvider.client';
import LoginModal from '../modals/LoginModal.client';
import SignupModal from '../modals/SignupModal.client';
import InviteFriendsModal from '../modals/InviteFriendsModal.client';
import {wispNewPage} from '../../utils/wisepopups.client';

interface HeaderProps {
  alert: SiteTopAlertBarType;
  menus: ReadonlyArray<MainNavigationCategoryType>;
}

export default function Header({alert, menus}: HeaderProps): ReactElement {
  const {
    openModal: openSignupModal,
    closeModal: closeSignupModal,
    isOpen: isSignupModalOpen,
  } = useModalToggle('signup');

  const {
    openModal: openInviteFriendsModal,
    closeModal: closeInviteFriendsModal,
    isOpen: isInviteFriendsModalOpen,
  } = useModalToggle('inviteFriends');

  const {
    isSubmenuActive,
    isAlertDisabledActive,
    setIsNoScrollActive,
    setIsProfileDropdownActive,
    setIsScrolled,
    setIsSubmenuActive,
    setIsUserMenuActive,
    setIsBottomOfPage,
  } = useGlobalContainer();

  useEffect(() => {
    let fixedHeaderTimeout: NodeJS.Timeout;
    window.addEventListener('scroll', () => {
      const scroll = window.scrollY;
      clearTimeout(fixedHeaderTimeout);

      fixedHeaderTimeout = setTimeout(() => {
        setIsScrolled(scroll > 36);
        setIsBottomOfPage(
          window.innerHeight + window.scrollY >=
            document.body.offsetHeight - 10,
        );
      });
    });
  });

  const {toggleSearch} = useSearchUI();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((value) => !value);
  }, []);

  useEffect(() => {
    setIsNoScrollActive(isMobileMenuOpen || isSubmenuActive);
  }, [isMobileMenuOpen, isSubmenuActive, setIsNoScrollActive]);

  const [activeSubmenu, _setActiveSubmenu] =
    useState<MainNavigationCategoryType['category_name']>(null);

  const setActiveSubmenu = useCallback(
    (categoryName) => {
      _setActiveSubmenu(categoryName);
      setIsSubmenuActive(Boolean(categoryName));
    },
    [setIsSubmenuActive],
  );

  const handleSubmenuClose = useCallback(() => {
    setActiveSubmenu(null);
  }, [setActiveSubmenu]);

  const {authUser, closeLoginModal, isLoginModalOpen} = useAuthService();

  const homepageLink = useMemo(() => (!authUser ? '/' : '/home'), [authUser]);

  const {pathname} = useUrl();

  useEffect(() => wispNewPage(), [pathname]);

  useEffect(() => {
    if (pathname) {
      setIsProfileDropdownActive(false);
      setIsMobileMenuOpen(false);
      setActiveSubmenu(null);
      setIsUserMenuActive(false);
    }
  }, [
    pathname,
    setActiveSubmenu,
    setIsProfileDropdownActive,
    setIsUserMenuActive,
  ]);

  const computedMenus = useMemo(() => {
    if (!authUser || !authUser?.member?.is_rewards_member) {
      return [
        ...menus.slice(0, 2),
        {
          columns: [],
          category_name: MainNavigationCategoryName.rewards,
        },
        ...menus.slice(2),
      ];
    }
    return menus;
  }, [authUser, menus]);

  return (
    <div className="main-header-outer js-main-header-outer">
      <header className="main-header js-main-header">
        <div
          className={classNames('main-header__before', {
            disabled: isAlertDisabledActive,
          })}
          id="main-header__before"
        >
          <div className="main-header__before-content">
            <i className="icon-free-shipping-2" />
            <span dangerouslySetInnerHTML={{__html: alert.content}} />
          </div>
        </div>
        <div className="main-header__rowlogo">
          <div className="main-header__rowTop container">
            <div className="main-header__rowTop-left">
              <div className="main-header__menu-opener">
                <button
                  type="button"
                  className={classNames('menu-opener js-menu-opener', {
                    active: isMobileMenuOpen,
                  })}
                  onClick={toggleMobileMenu}
                >
                  <span className="menu-opener__inner js-menu-opener__inner">
                    <span className="menu-opener__lines" />
                  </span>
                </button>
              </div>
              <div className="main-header__logo">
                <Link to={homepageLink} className="main-header__logo-link">
                  <img
                    src={theWhiskyListLogoDesktopSVG}
                    className="main-header__logo-img"
                    alt="The Whisky List"
                  />{' '}
                </Link>
                <Link
                  to="/news/icons-of-whisky-recognises-aussie-retailer-the-whi"
                  className="icons-of-whisky"
                  title="Icons of Whisky Rest of World 2022"
                >
                  <img
                    src={IconsOfWhisky}
                    className="icons-of-whisky__img"
                    alt="Icons of Whisky Rest of World 2022"
                  />
                </Link>
              </div>
              <div className="main-header__logo-mobile">
                <Link
                  to={homepageLink}
                  className="main-header__logo-link__mobile"
                >
                  <img
                    src={theWhiskyListLogoSmallPNG}
                    className="main-header__logo-img-mobile"
                    alt="The Whisky List"
                  />
                </Link>
              </div>
            </div>
            <div className="main-header__rowTop-right">
              <div
                role="button"
                tabIndex={-1}
                className="main-header__search"
                id="mainHeaderSearch"
                onClick={toggleSearch}
              >
                <span className="main-header__search-placeholder large">
                  Search for whisky &amp; more...
                </span>
                <span className="main-header__search-placeholder small">
                  Search for whisky...
                </span>
                <button type="submit" className="main-header__search-submit">
                  <i className="icon-search" />
                </button>
              </div>
              <div
                role="button"
                tabIndex={-1}
                className="main-header__search-trigger"
                id="mobileSearchTrigger"
                onClick={toggleSearch}
              >
                <i className="icon-search" />
              </div>
              <UserMenu
                openInviteFriendsModal={openInviteFriendsModal}
                openSignupModal={openSignupModal}
              />
              <div className="main-header__right-container">
                <WispNotificationButton />
                <CartMenuButton />
              </div>
            </div>
          </div>
        </div>
        <div className="main-header__rowmenu" id="menu-area">
          <div className="main-header__menu container">
            <nav
              className={classNames(
                'main-header__menu main-menu js-main-menu',
                {
                  active: isMobileMenuOpen,
                },
              )}
            >
              <div className="main-header__menu-mobile-header">
                <Link to={homepageLink}>
                  <Image
                    src={theWhiskyListLogoMobileSVG}
                    width={76}
                    height={25}
                    alt="The Whisky List"
                  />
                </Link>
                <Link
                  to="/news/icons-of-whisky-recognises-aussie-retailer-the-whi"
                  className="icons-of-whisky"
                  title="Icons of Whisky Rest of World 2022"
                >
                  <img
                    src={IconsOfWhisky}
                    className="icons-of-whisky__img"
                    alt="Icons of Whisky Rest of World 2022"
                  />
                </Link>
                <div
                  role="button"
                  tabIndex={-1}
                  onClick={handleSubmenuClose}
                  className="main-header__menu-mobile-submenu-title"
                  id="submenu-mobile-title-close"
                >
                  <div className="main-header__menu-mobile-submenu-title-icon">
                    <i className="icon-arrow-point-to-right" />
                  </div>
                  <span id="submenu-mobile-title" />
                </div>
                <div className="main-header__menu-opener">
                  <button
                    type="button"
                    className={classNames('menu-opener js-menu-opener', {
                      active: isMobileMenuOpen,
                    })}
                    onClick={toggleMobileMenu}
                  >
                    <span className="menu-opener__inner js-menu-opener__inner">
                      <span className="menu-opener__lines" />
                    </span>
                  </button>
                </div>
              </div>

              <div
                role="button"
                tabIndex={-1}
                className="main-header__search main-header__search-mobile"
                onClick={toggleSearch}
              >
                <span className="main-header__search-placeholder small">
                  Search for whisky...
                </span>
                <button type="submit" className="main-header__search-submit">
                  <i className="icon-search" />
                </button>
              </div>
              <div className="main-menu__inner">
                <ul
                  className={classNames('main-menu__list js-main-menu__list', {
                    active: isMobileMenuOpen,
                  })}
                >
                  {computedMenus.map((menu) => (
                    <HeaderNavigationMenu
                      key={menu.category_name}
                      menu={menu}
                      isActive={menu.category_name === activeSubmenu}
                      setIsActive={setActiveSubmenu}
                      onNavigate={closeMobileMenu}
                    />
                  ))}
                </ul>
              </div>
            </nav>
          </div>
          <div className="main-header__submenu" />
        </div>
      </header>
      <LoginModal isOpen={isLoginModalOpen} onRequestClose={closeLoginModal} />
      <SignupModal
        isOpen={isSignupModalOpen}
        onRequestClose={closeSignupModal}
      />
      <InviteFriendsModal
        isOpen={isInviteFriendsModalOpen}
        onRequestClose={closeInviteFriendsModal}
      />
    </div>
  );
}

interface UserMenuProps {
  openSignupModal: () => void;
  openInviteFriendsModal: () => void;
}

function UserMenu({openSignupModal, openInviteFriendsModal}: UserMenuProps) {
  const {setIsProfileDropdownActive, isProfileDropdownActive} =
    useGlobalContainer();

  const toggleProfileDropdownActive = useCallback(
    () => setIsProfileDropdownActive((active) => !active),
    [setIsProfileDropdownActive],
  );

  const {setIsUserMenuActive} = useGlobalContainer();
  const toggleUserMenu = useCallback(
    () => setIsUserMenuActive((active) => !active),
    [setIsUserMenuActive],
  );

  const menuBtnRef = useRef(null);

  const clickOutsideHandler = useCallback(
    (e) => {
      !menuBtnRef?.current?.contains(e.target) &&
        setIsProfileDropdownActive(false);
      !menuBtnRef?.current?.contains(e.target) && setIsUserMenuActive(false);
    },
    [setIsProfileDropdownActive, setIsUserMenuActive],
  );

  useEffect(() => {
    document.addEventListener('click', clickOutsideHandler);
    return () => document.removeEventListener('click', clickOutsideHandler);
  }, [clickOutsideHandler]);

  const {authUser, logout, openLoginModal} = useAuthService();
  const userInitials = useMemo((): string => {
    if (authUser) {
      const {first_name, last_name} = authUser;
      if (first_name && last_name) {
        return (first_name[0] + last_name[0]).toUpperCase();
      }
    }
    return '';
  }, [authUser]);

  if (!authUser) {
    return (
      <>
        <div className="main-header__user">
          <span
            role="button"
            tabIndex={0}
            onClick={() => openLoginModal()}
            className="main-header__link sign-in-button"
          >
            Sign In
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={openSignupModal}
            className="button join-button"
          >
            Join
          </span>
        </div>
        <div className="main-header__user-mob">
          <div
            ref={menuBtnRef}
            role="button"
            tabIndex={-1}
            className="main-header__user-mob-button"
            id="mobileUserTrigger"
            onClick={toggleUserMenu}
          >
            <i className="icon-profile" />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="main-header__user">
        <Link to="/my/profile" className="button button-bigger">
          <span className="button_text_xxl">Whisky Lover</span>
          <span> Dashboard</span>
        </Link>
      </div>
      <div
        ref={menuBtnRef}
        role="button"
        tabIndex={-1}
        onClick={toggleProfileDropdownActive}
        className={classNames(
          'main-header__user-profile main-header__user-profile__image',
          {'profile-dropdown-active': isProfileDropdownActive},
        )}
        id="profile-dropdown-trigger"
      >
        {authUser.member.profile_picture ? (
          <Image
            src={authUser.member.profile_picture}
            width={40}
            height={40}
            alt="Profile picture"
          />
        ) : (
          <span>{userInitials}</span>
        )}
      </div>
      <div className="main-header__user-dropdown" id="profile-dropdown">
        <ul>
          <li>
            <Link className="main-header__profile-menu-item" to="/my/profile">
              <div className="icon-wrapper">
                <i className="icon-whisky-lovers-australia" />
              </div>
              <span>Dashboard</span>
            </Link>
          </li>
          <li>
            <Link
              className="main-header__profile-menu-item"
              to="/my/profile/edit"
            >
              <div className="icon-wrapper">
                <i className="icon-profile" />
              </div>
              <span>Profile</span>
            </Link>
          </li>
          <li>
            <span
              role="button"
              tabIndex={-1}
              onClick={openInviteFriendsModal}
              className="main-header__profile-menu-item"
            >
              <div className="icon-wrapper">
                <i className="icon-friends" />
              </div>
              <span>Invite Friends</span>
            </span>
          </li>
          <li>
            <Link
              className="main-header__profile-menu-item"
              to="/my/communications"
            >
              <div className="icon-wrapper">
                <i className="icon-communications" style={{fontSize: '9px'}} />
              </div>
              <span>Communications</span>
            </Link>
          </li>
          <li>
            <Link className="main-header__profile-menu-item" to="/my/orders">
              <div className="icon-wrapper">
                <i className="icon-orders" style={{fontSize: '9px'}} />
              </div>
              <span>Order History</span>
            </Link>
          </li>
          <li>
            <Link className="main-header__profile-menu-item" to="/my/events">
              <div className="icon-wrapper">
                <i className="icon-whisky-events" style={{fontSize: '9px'}} />
              </div>
              <span>My Events</span>
            </Link>
          </li>
          {/* <li>
            <Link to="/my/credits">
              <div className="icon-wrapper">
                <i className="icon-credits" />
              </div>
              <span>Credits</span>
            </Link>
          </li>
          <li>
            <Link to="/my/addresses">
              <div className="icon-wrapper">
                <i className="icon-adress" />
              </div>
              <span>Addresses</span>
            </Link>
          </li>
          <li>
            <Link to="/my/cards">
              <div className="icon-wrapper">
                <i className="icon-credit-cards" />
              </div>
              <span>Credit Cards</span>
            </Link>
          </li> */}
          <li>
            <span
              role="button"
              tabIndex={-1}
              onClick={logout}
              className="main-header__profile-menu-item"
            >
              <div className="icon-wrapper">
                <i className="icon-logout" />
              </div>
              <span>Logout</span>
            </span>
          </li>
        </ul>
      </div>
    </>
  );
}

function WispNotificationButton() {
  return (
    <div
      role="button"
      tabIndex={-1}
      className="main-header__notifications wisp"
    >
      <svg
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        width="22"
        height="22"
        viewBox="0 0 24 24"
        stroke="black"
        xmlns="<http://www.w3.org/2000/svg>"
      >
        <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
      </svg>
    </div>
  );
}

/* eslint-enable hydrogen/prefer-image-component */
